"use client";

import * as React from "react";
import { OTPInput, REGEXP_ONLY_DIGITS, type SlotProps } from "input-otp";
import styles from "./OtpCodeInput.module.css";

// The six-digit code every TWT sign-in asks for (TWT's own code, the Whop
// code, the account email change). input-otp keeps one real input under the
// six slots, so the phone's code suggestion from Mail or Messages, password
// managers and pasting "123 456" or "123-456" all fill it in one go. The
// slots read the surface's colour variables (SignInModalStyles.tsx), so the
// same input fits the dark sign-in window and light pages.

export type OtpCodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  name?: string;
  label?: string;
  describedBy?: string;
  errorMessageId?: string;
  autoFocus?: boolean;
};

export const OtpCodeInput = React.forwardRef<HTMLInputElement, OtpCodeInputProps>(
  function OtpCodeInput(
    {
      value,
      onChange,
      onComplete,
      disabled = false,
      invalid = false,
      id,
      name = "code",
      label = "Verification code",
      describedBy,
      errorMessageId,
      autoFocus = true,
    },
    ref
  ) {
    return (
      <OTPInput
        ref={ref}
        id={id}
        name={name}
        value={value}
        onChange={(next) => onChange(next.replace(/\D/g, "").slice(0, 6))}
        onComplete={onComplete}
        maxLength={6}
        pattern={REGEXP_ONLY_DIGITS}
        pasteTransformer={(pasted) => pasted.replace(/\D/g, "").slice(0, 6)}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        disabled={disabled}
        aria-label={label}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        aria-errormessage={invalid ? errorMessageId : undefined}
        pushPasswordManagerStrategy={
          typeof document !== "undefined" &&
          typeof document.elementFromPoint === "function"
            ? "increase-width"
            : "none"
        }
        containerClassName={`${styles.otp}${invalid ? ` ${styles.invalid}` : ""}${
          disabled ? ` ${styles.disabled}` : ""
        }`}
        render={({ slots }) => (
          <>
            <div className={styles.group}>
              {slots.slice(0, 3).map((slot, index) => (
                <Slot key={index} {...slot} />
              ))}
            </div>
            <span className={styles.separator} aria-hidden="true" />
            <div className={styles.group}>
              {slots.slice(3).map((slot, index) => (
                <Slot key={index + 3} {...slot} />
              ))}
            </div>
          </>
        )}
      />
    );
  }
);

function Slot({ char, hasFakeCaret, isActive }: SlotProps) {
  return (
    <div
      data-otp-slot={char ? "filled" : isActive ? "active" : "empty"}
      className={`${styles.slot}${isActive ? ` ${styles.active}` : ""}${
        char ? ` ${styles.filled}` : ""
      }`}
    >
      {char}
      {hasFakeCaret ? <span className={styles.caret} /> : null}
    </div>
  );
}
